import { FunctionComponent as FC, useState, useMemo } from 'react';
import { withStyles, TextField, Table, Card, CollapsibleCard, formattedCurrency, formatDate } from 'vopay';
import type { SupportedCurrencies } from 'vopay';
import styles from './BankAccountTransactions.module.scss';
import type { TransactionData } from '@/hooks/usePaymentMethodDetails';
import ExportMenu from './ExportMenu/ExportMenu';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type BankAccountTransactionsProps = {
  balance: string;
  transactionData: TransactionData;
  currency: SupportedCurrencies;
};

const BankAccountTransactions: FC<BankAccountTransactionsProps> = ({ balance, transactionData, currency }) => {
  const [transactionKeyword, setTransactionKeyword] = useState('');
  const { t } = useTranslation('translations', {
    keyPrefix: 'Customers.PaymentMethodsTab.Details.BankAccountTransactions',
  });

  const filteredTransactions = useMemo(() => {
    return transactionData.transactions.slice(0, 50).filter((tx) => {
      return (
        tx.Description.toLowerCase().includes(transactionKeyword.toLowerCase()) ||
        tx.Balance.toString().includes(transactionKeyword) ||
        tx.TransactionDate.includes(transactionKeyword) ||
        tx.Debit?.toString().includes(transactionKeyword) ||
        tx.Credit?.toString().includes(transactionKeyword)
      );
    });
  }, [transactionKeyword, transactionData.transactions]);

  return (
    <CollapsibleCard className={cn('balanceTransactionHistory')}>
      <CollapsibleCard.Trigger className={cn('trigger')} indicator="chevron">
        <div className={cn('triggerContent')}>
          <h3>{t('title', { defaultValue: 'Bank Account Balance' })}</h3>

          <Card
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              shadowOpacity: 0.13,
              borderColor: '#2d2d2d1a',
            }}
            className={cn('balanceCard')}
          >
            <b>{t('balance', { defaultValue: 'Balance' })}</b>
            {'  '}
            {currency} {formattedCurrency(balance)}
          </Card>
        </div>
      </CollapsibleCard.Trigger>

      <CollapsibleCard.Content asChild className={cn('content')}>
        <div className={cn('contentInner')}>
          <p className={cn('lastRetrieved')}>
            {t('lastRetrieved', { defaultValue: 'Last Retrieved' })}:{' '}
            {formatDate(transactionData.lastRetrievedTime || '')}
          </p>

          <div className={cn('contentHeader')}>
            <TextField
              name="keywordSearch"
              value={transactionKeyword}
              onChange={(e) => setTransactionKeyword(e.target.value)}
              variant="outlineShadow"
              placeholder="Keywords"
            />

            <div className={cn('exportMenu')}>
              <ExportMenu />
            </div>
          </div>

          <div className={cn('tableContainer', 'scrollbar')}>
            <Table
              version="2"
              id="paas-transaction-history"
              zebra
              rows={filteredTransactions.map((t) => ({
                id: t.AggregatorTransactionID,
                data: {
                  date: formatDate(t.TransactionDate || ''),
                  credit: t.Credit === null ? '' : `$${t.Credit.toLocaleString()}`,
                  debit: t.Debit === null ? '' : `$${t.Debit.toLocaleString()}`,
                  balance: `$${t.Balance === null ? `0` : t.Balance.toLocaleString()}`,
                  description: t.Description,
                },
              }))}
              columns={[
                {
                  id: 'date',
                  header: t('date', { defaultValue: 'Date' }),
                  field: 'date',
                  style: {
                    whiteSpace: 'nowrap',
                  },
                },
                {
                  id: 'credit',
                  header: t('credit', { defaultValue: 'Credit' }),
                  field: 'credit',
                },
                {
                  id: 'debit',
                  header: t('debit', { defaultValue: 'Debit' }),
                  field: 'debit',
                },
                {
                  id: 'balance',
                  header: t('balance', { defaultValue: 'Balance' }),
                  field: 'balance',
                },
                {
                  id: 'description',
                  header: t('description', { defaultValue: 'Description' }),
                  field: 'description',
                },
              ]}
            />
          </div>
        </div>
      </CollapsibleCard.Content>
    </CollapsibleCard>
  );
};

export default BankAccountTransactions;
